import type { Listing } from '@/types/listing';

/**
 * A shared home's conversation, minus the network.
 *
 * `/shared/[token]` is the first page in this product that renders text one
 * person wrote for another, and the first one whose reader has no account. Both
 * of those put rules here rather than in the component:
 *
 * - **The limits must match the server's, in the server's units.** Every bound
 *   below is the Go constant it mirrors (`internal/storage/share_repository.go`),
 *   and the counting is done the way Go counts.
 * - **Who you are lives in this browser and nowhere else.** An anonymous
 *   participant cannot be recognised across devices — that is the honest limit
 *   of a link — so `participantId` is stored locally, per token, and the storage
 *   is guarded because a browser in private mode will refuse it.
 */

// MARK: - Wire shapes (GET /api/v1/shares/{token})

/** Where one person landed. The server rejects anything else with a 400. */
export type Decision = 'love' | 'maybe' | 'pass';

/** Fixed order — how the buttons read, and how the summary groups. */
export const DECISIONS: readonly Decision[] = ['love', 'maybe', 'pass'] as const;

export function isDecision(value: unknown): value is Decision {
  return typeof value === 'string' && (DECISIONS as readonly string[]).includes(value);
}

export interface ShareDecisionRow {
  /** Display name, already resolved by the API — participant ids never leave. */
  participant: string;
  decision: Decision;
  updatedAt: string;
}

export interface ShareComment {
  id: string;
  participant: string;
  body: string;
  createdAt: string;
}

export interface SharedHomeView {
  listing: Listing;
  /** What the sharer wrote when they sent it. Optional, and usually absent. */
  message?: string;
  /**
   * The sharer's first name, best-effort.
   *
   * **Absent for a real fraction of accounts, by design.** `ViewShare` looks the
   * owner up and returns `""` rather than a guess when `users` has no reliable
   * name — every Firebase signup whose provider gave none — and on any failed
   * lookup, so that a nicety can never cost the page the house. The UI must
   * therefore read as a finished sentence without it, not as a blank where a
   * name should be.
   */
  sharedBy?: string;
  decisions: ShareDecisionRow[];
  comments: ShareComment[];
  /** Where to send somebody who does not have the app. Configuration, not a constant. */
  installUrl?: string;
}

/** Who the viewer is, as this browser knows them. */
export interface ShareParticipant {
  id: string;
  /** The name the SERVER stored, which is not always the one that was typed. */
  name: string;
}

// MARK: - Limits, counted the way the server counts

/** `storage.MaxCommentLength`. */
export const MAX_COMMENT_LENGTH = 1000;

/** `storage.MaxDisplayNameLength`. */
export const MAX_DISPLAY_NAME_LENGTH = 60;

/**
 * Length in **code points**, because the server bounds `len([]rune(body))`.
 *
 * `"🏡".length` is 2 in JavaScript (UTF-16 code units) and 1 in Go (runes). A
 * client that counts the JavaScript way disagrees with the only counter that
 * decides anything: it would refuse a comment the API would have accepted, and —
 * worse for a first-time user — show "1002 / 1000" under text that is plainly
 * a thousand characters. Spreading a string iterates code points, which is what
 * `range` over a Go string does.
 */
export function runeLength(text: string): number {
  return [...text].length;
}

/**
 * Why this comment cannot be posted, in words, or `null` when it can.
 *
 * The server enforces both of these itself (`AddComment`); this exists so the
 * reader is told *before* they lose the text to a 400 whose body is a bare
 * sentence with no idea which field it is about.
 */
export function commentProblem(body: string): string | null {
  if (body.trim() === '') return 'Write something first.';
  const over = runeLength(body.trim()) - MAX_COMMENT_LENGTH;
  if (over > 0) {
    return `That's ${over.toLocaleString()} ${over === 1 ? 'character' : 'characters'} past the ${MAX_COMMENT_LENGTH.toLocaleString()}-character limit — trim it and it will send.`;
  }
  return null;
}

/**
 * Why this display name cannot be used, or `null`.
 *
 * The server is more forgiving than this: a blank name becomes "Someone" and a
 * long one is truncated at 60. Both are silent, and both would put a name on the
 * thread that the person did not choose — so we ask here instead of letting the
 * database decide who they are.
 */
export function displayNameProblem(name: string): string | null {
  if (name.trim() === '') return 'Add a name so the others know who reacted.';
  if (runeLength(name.trim()) > MAX_DISPLAY_NAME_LENGTH) {
    return `Names are limited to ${MAX_DISPLAY_NAME_LENGTH} characters.`;
  }
  return null;
}

// MARK: - Who this browser is, per share

/** One key per token: a person can hold links to several homes at once. */
export function participantKey(token: string): string {
  return `goreal:share-participant:${token}`;
}

export function readParticipant(token: string): ShareParticipant | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(participantKey(token));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ShareParticipant> | null;
    // A half-written or hand-edited record is treated as no record at all: the
    // page then asks for a name again, which is recoverable. Posting with an id
    // the server has never seen is not — the comment 500s and the text is gone.
    if (!parsed || typeof parsed.id !== 'string' || parsed.id === '') return null;
    const name = typeof parsed.name === 'string' && parsed.name.trim() !== '' ? parsed.name : 'You';
    return { id: parsed.id, name };
  } catch {
    return null;
  }
}

export function writeParticipant(token: string, participant: ShareParticipant): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(participantKey(token), JSON.stringify(participant));
  } catch {
    // Storage unavailable (private mode, quota). The reaction still posted; the
    // only cost is being asked for a name again next visit.
  }
}

// MARK: - Reading the thread back

/**
 * Fold one person's new answer into the list.
 *
 * `POST /decision` answers **204 with no body**, so the only way the page can
 * show the reaction it just saved is to place it itself. A decision REPLACES
 * that participant's previous one server-side (`SetDecision` upserts), so this
 * must replace in place rather than append — otherwise changing your mind from
 * "love" to "pass" would show you standing in both camps.
 *
 * Matched on display name, which is the only identity the public view exposes.
 * Two participants who chose the same name are indistinguishable here; they are
 * indistinguishable on the rendered page too, so this does not invent a
 * confusion the reader would not already have. The authoritative list arrives
 * with the next successful refresh.
 */
export function mergeDecision(
  rows: ShareDecisionRow[],
  row: ShareDecisionRow
): ShareDecisionRow[] {
  const at = rows.findIndex((r) => r.participant === row.participant);
  if (at === -1) return [...rows, row];
  const next = [...rows];
  next[at] = row;
  return next;
}

/** The rows that landed on each answer, in `DECISIONS` order. */
export function groupDecisions(rows: ShareDecisionRow[]): Record<Decision, ShareDecisionRow[]> {
  const out: Record<Decision, ShareDecisionRow[]> = { love: [], maybe: [], pass: [] };
  for (const row of rows) {
    // A decision the server does not recognise cannot reach this list, but the
    // page is public and the payload is not ours — an unknown value is dropped
    // rather than rendered as an empty column.
    if (isDecision(row.decision)) out[row.decision].push(row);
  }
  return out;
}

/** How each answer is labelled everywhere: the button, the tally, the summary. */
export const DECISION_COPY: Record<Decision, { verb: string; heading: string; said: string }> = {
  love: { verb: 'Love it', heading: 'Loved it', said: 'loves this one' },
  maybe: { verb: 'Maybe', heading: 'On the fence', said: 'is on the fence' },
  pass: { verb: 'Pass', heading: 'Passed', said: 'would pass' },
};

/**
 * When a comment was written, as a person would say it.
 *
 * **Relative, and computed from epoch milliseconds**, which is the one form of
 * this that cannot disagree between the server render and the hydrated client:
 * the same two numbers subtract to the same answer in every timezone. A
 * `toLocaleString()` timestamp would render in UTC on the server and in the
 * reader's zone in the browser, and this component is server-rendered like every
 * other client component in a Next app.
 *
 * Older than a week it falls back to a date, pinned to `en-US` **and UTC** for
 * the same reason. Nothing is claimed for a timestamp we cannot parse — the
 * caller renders no time at all rather than "Invalid Date".
 */
export function formatCommentTime(iso: string, now: number = Date.now()): string {
  const at = new Date(iso).getTime();
  if (!Number.isFinite(at)) return '';
  const seconds = Math.max(0, Math.round((now - at) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

// MARK: - Failures, in words

/**
 * What to tell somebody whose reaction or comment did not save.
 *
 * Only the statuses these three endpoints actually produce are named. `Join`,
 * `SetDecision` and `AddComment` all resolve the token first, so **404 is a dead
 * link** — the one failure that will never fix itself by trying again, and the
 * one where "try again" would be a lie. 410 is deliberately absent: only
 * `ViewShare` answers it (the link is fine, the house is gone), and that case is
 * handled before this page ever renders a composer.
 */
export function shareActionMessage(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  if (status === 404) {
    return 'This link has been turned off, so nothing new can be added to it. Ask whoever sent it for a fresh one.';
  }
  if (status === 400) {
    return "We couldn't save that — check what you wrote and send it again.";
  }
  return "We couldn't save that just now. Nothing was lost — try again in a moment.";
}

/**
 * What to tell an owner whose share link could not be minted.
 *
 * `CreateShare` is the one endpoint in this feature behind a session, so **401
 * is a real outcome** and not a generic error: a token can expire in an open
 * tab, and "something went wrong" would send somebody to reload the page when
 * signing in again is the fix. 404 means the home itself has gone — the handler
 * checks the listing exists before minting, precisely so a recipient never opens
 * a link to nothing.
 *
 * The bare `Authentication required` string is matched too, because that one is
 * thrown by `fetchAPI` **before any request is made** when the session flag is
 * set but Firebase hands back no token — the same problem, arriving without a
 * status code.
 */
export function shareCreateMessage(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  const message = (err as { message?: string } | null)?.message ?? '';
  if (status === 401 || status === 403 || message === 'Authentication required') {
    return 'Your session has expired — sign in again and the link will work.';
  }
  if (status === 404) {
    return 'This home is no longer available, so there is nothing to share.';
  }
  return "We couldn't create a share link just now. Try again in a moment.";
}
